import { HighwayArea, HighwayPoiSpec, RackingArea, Segment } from '@thive/fddm-types';

import { NumUtils } from '@/modules/common/utils';
import { FDDM_TAG } from '@modules/floorplanDesign/constants';
import { HIGHWAY_ARROW_ID, Z_INDEX_MAP } from '../constants/elements';
import { elementFactory } from '../factories';
import { calcHighwayArrowScale, calcRackScale } from '../helpers';

export type ArrowImageType =
  | 'chevron-double-right'
  | 'chevron-double-both'
  | 'chevron-double-left-hand-traffic'
  | 'chevron-double-right-hand-traffic';

const getSegmentLength = (segment: Segment): number =>
  Math.hypot(segment.end.x - segment.start.x, segment.end.y - segment.start.y);

const getSegmentWidth = (segment: Segment): number =>
  segment.leftSideWidth + segment.rightSideWidth;

const getSegmentAngle = (segment: Segment): number =>
  Math.atan2(segment.end.y - segment.start.y, segment.end.x - segment.start.x);

const getGenerationDirectionOffset = (segment: Segment): number => {
  const segmentLength = getSegmentLength(segment);
  const segmentWidth = getSegmentWidth(segment);

  // The backend uses the wider axis as the generation direction when
  // the segment length is not greater than the combined highway width.
  return segmentLength > segmentWidth ? 0 : Math.PI / 2;
};

const getSegmentCenter = (segment: Segment) => ({
  centerX: (segment.start.x + segment.end.x) / 2,
  centerY: (segment.start.y + segment.end.y) / 2,
});

const getHighwayImageSvgName = (roadType: HighwayPoiSpec['roadType']): ArrowImageType => {
  switch (roadType) {
    case 'ONE_WAY':
      return 'chevron-double-right';
    case 'TWO_WAY_RIGHT_HAND_TRAFFIC':
      return 'chevron-double-right-hand-traffic';
    case 'TWO_WAY_LEFT_HAND_TRAFFIC':
      return 'chevron-double-left-hand-traffic';
    case 'TWO_WAY_INDIA_STYLE':
    default:
      return 'chevron-double-both';
  }
};

/* TODO: We need a way to have a arrowElement for each point on the highway... probably another function */
export const createArrowElement = (highway: HighwayArea, zoom: number) => {
  const newScale = NumUtils.mmToMeters(calcHighwayArrowScale(zoom) * 2);
  const { roadType } = highway.highwayPoiSpecs[0];
  // const url = `${process.env.PUBLIC_URL}/${getHighwayImageSvgName(roadType)}.svg`;
  const segment = highway.segments[0];
  const localRotation = segment ? getGenerationDirectionOffset(segment) : 0;
  const textureVariant = getHighwayImageSvgName(roadType);

  return elementFactory.buildRectangleElement({
    id: `${highway.id}-${HIGHWAY_ARROW_ID}`,
    parentId: highway.id,
    rectangle: { angle: localRotation, centerX: 0, centerY: 0, width: newScale, length: newScale },
    tags: [FDDM_TAG.HIGHWAY_ARROW],
    interactivity: {
      selectable: false,
      rotatable: false,
      draggable: false,
    },
    textureVariant,
    z: Z_INDEX_MAP[FDDM_TAG.HIGHWAY_ARROW],
  });
};

export const createSegmentArrowElement = (highway: HighwayArea, segment: Segment, zoom: number) => {
  const newScale = NumUtils.mmToMeters(calcHighwayArrowScale(zoom));
  const { roadType } = highway.highwayPoiSpecs[0];
  const { centerX, centerY } = getSegmentCenter(segment);
  const angle = getSegmentAngle(segment) + getGenerationDirectionOffset(segment);

  return elementFactory.buildRectangleElement({
    id: `${segment.id}-${HIGHWAY_ARROW_ID}`,
    parentId: highway.id,
    // Angled highways use absolute segment points and have no parent
    // rotation, so the segment angle and generation-axis offset are combined.
    rectangle: { angle, centerX, centerY, width: newScale, length: newScale },
    tags: [FDDM_TAG.HIGHWAY_ARROW],
    interactivity: {
      selectable: false,
      rotatable: false,
      draggable: false,
    },
    textureVariant: getHighwayImageSvgName(roadType),
    z: Z_INDEX_MAP[FDDM_TAG.HIGHWAY_ARROW],
  });
};

export const createRackImage = (area: RackingArea, zoom: number) => {
  const newScale = NumUtils.mmToMeters(calcRackScale(zoom));

  return elementFactory.buildRectangleElement({
    id: `${area.id}-Logo`,
    parentId: area.id,
    rectangle: { angle: 0, centerX: 0, centerY: 0, width: newScale, length: newScale },
    tags: [FDDM_TAG.RACK],
    interactivity: {
      selectable: false,
      rotatable: false,
      draggable: false,
    },
    textureVariant: 'rack',
    z: Z_INDEX_MAP[FDDM_TAG.RACK],
  });
};
